import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Database, FileText, LucideAngularModule, RefreshCcw, Search } from 'lucide-angular';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { AllReaRegionCount, AllReaTableRow } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { ReaReportsService } from '../../rea-reports/services/rea-reports.service';

type ReaRegionCode = 'NRPC' | 'SRPC' | 'ERPC' | 'WRPC' | 'NERPC';

@Component({
  standalone: true,
  selector: 'app-all-rea-page',
  imports: [CommonModule, FormsModule, RouterLink, LucideAngularModule],
  templateUrl: './all-rea.page.html',
  styleUrl: './all-rea.page.scss',
})
export class AllReaPage implements OnInit, OnDestroy {
  private static readonly START_YEAR = 2025;
  private searchDebounceId: ReturnType<typeof setTimeout> | null = null;
  private latestRequestId = 0;
  private latestCountRequestId = 0;
  private readonly reaReportsService = inject(ReaReportsService);
  private readonly toast = inject(ToastService);

  protected readonly FileTextIcon = FileText;
  protected readonly DatabaseIcon = Database;
  protected readonly SearchIcon = Search;
  protected readonly RefreshCcwIcon = RefreshCcw;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;

  protected readonly search = signal('');
  protected readonly selectedRea = signal<ReaRegionCode | ''>('');
  protected readonly selectedMonth = signal<number | ''>('');
  protected readonly selectedYear = signal<number | ''>('');
  protected readonly loading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;

  private readonly regionCounts = signal<Record<ReaRegionCode, number>>({
    NRPC: 0,
    SRPC: 0,
    ERPC: 0,
    WRPC: 0,
    NERPC: 0,
  });
  protected readonly tableRows = signal<AllReaTableRow[]>([]);
  protected readonly totalRows = signal(0);

  protected readonly summaryTiles = computed(() =>
    this.reaOptions().map((code) => ({
      label: code,
      count: this.regionCounts()[code],
      icon: this.DatabaseIcon,
    })),
  );

  protected readonly monthOptions = computed(() => Array.from({ length: 12 }, (_, index) => index + 1));
  protected readonly reaOptions = computed(() => ['NRPC', 'SRPC', 'ERPC', 'WRPC', 'NERPC'] as ReaRegionCode[]);
  protected readonly yearOptions = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - AllReaPage.START_YEAR + 1 }, (_, index) => currentYear - index);
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRows() / this.pageSize())));
  protected readonly startRow = computed(() => (this.totalRows() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.page() * this.pageSize(), this.totalRows()));

  ngOnInit(): void {
    this.applyDefaultFilters();
    this.loadOverview();
  }

  ngOnDestroy(): void {
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
    }
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.page.set(1);
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
    }
    this.searchDebounceId = setTimeout(() => {
      this.loadOverview();
    }, 300);
  }

  protected updateRea(value: string): void {
    const matched = this.reaOptions().find((row) => row === value) ?? '';
    this.selectedRea.set(matched);
    this.page.set(1);
    this.loadOverview();
  }

  protected selectSummaryTile(region: ReaRegionCode): void {
    if (this.selectedRea() === region && this.page() === 1) {
      return;
    }
    this.selectedRea.set(region);
    this.page.set(1);
    this.loadOverview();
  }

  protected updateMonth(value: string): void {
    this.selectedMonth.set(value ? Number(value) : '');
    this.page.set(1);
    this.loadOverview();
  }

  protected updateYear(value: string): void {
    this.selectedYear.set(value ? Number(value) : '');
    this.page.set(1);
    this.loadOverview();
  }

  protected onPageSizeChange(value: string): void {
    this.pageSize.set(Number(value) || 10);
    this.page.set(1);
    this.loadOverview();
  }

  protected goToPage(nextPage: number): void {
    const targetPage = Math.max(1, Math.min(nextPage, this.totalPages()));
    if (targetPage === this.page()) {
      return;
    }
    this.page.set(targetPage);
    this.loadOverview();
  }

  protected refreshFilterData(): void {
    this.search.set('');
    this.selectedRea.set('');
    this.selectedMonth.set('');
    this.selectedYear.set('');
    this.page.set(1);
    this.loadOverview();
  }

  protected monthLabel(month: number): string {
    return new Date(2000, month - 1, 1).toLocaleString(undefined, { month: 'long' });
  }

  private applyDefaultFilters(): void {
    const now = new Date();
    const dayOfMonth = now.getDate();
    const defaultDate = new Date(now.getFullYear(), now.getMonth() - (dayOfMonth < 7 ? 1 : 0), 1);
    this.selectedRea.set('NRPC');
    this.selectedMonth.set(defaultDate.getMonth() + 1);
    this.selectedYear.set(defaultDate.getFullYear());
  }

  private loadOverview(): void {
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
      this.searchDebounceId = null;
    }
    this.loadSummaryCounts();
    const requestId = ++this.latestRequestId;
    this.loading.set(true);
    this.reaReportsService
      .getAllReaOverview({
        rpcRegion: this.selectedRea(),
        month: this.selectedMonth(),
        year: this.selectedYear(),
        search: this.search(),
        page: this.page(),
        pageSize: this.pageSize(),
      })
      .subscribe({
        next: (response) => {
          if (requestId !== this.latestRequestId) {
            return;
          }
          this.tableRows.set(response.rows || []);
          this.totalRows.set(Number(response.total || 0));
          this.page.set(Number(response.page || this.page()));
          this.loading.set(false);
        },
        error: (error) => {
          if (requestId !== this.latestRequestId) {
            return;
          }
          this.regionCounts.set(this.emptyRegionCounts());
          this.tableRows.set([]);
          this.totalRows.set(0);
          this.loading.set(false);
          const detail = error?.error?.detail || 'Failed to load All REA data';
          this.toast.show(detail, '', 'danger');
        },
      });
  }

  private loadSummaryCounts(): void {
    const requestId = ++this.latestCountRequestId;
    this.reaReportsService
      .getAllReaOverview({
        rpcRegion: '',
        month: this.selectedMonth(),
        year: this.selectedYear(),
        search: this.search(),
      })
      .subscribe({
        next: (response) => {
          if (requestId !== this.latestCountRequestId) {
            return;
          }
          this.regionCounts.set(this.extractRegionCounts(response.counts || []));
        },
        error: () => {
          if (requestId !== this.latestCountRequestId) {
            return;
          }
          this.regionCounts.set(this.emptyRegionCounts());
        },
      });
  }

  private extractRegionCounts(counts: AllReaRegionCount[]): Record<ReaRegionCode, number> {
    const nextCounts = this.emptyRegionCounts();
    for (const item of counts) {
      if (item.rpc_region in nextCounts) {
        nextCounts[item.rpc_region as ReaRegionCode] = Number(item.count || 0);
      }
    }
    return nextCounts;
  }

  private emptyRegionCounts(): Record<ReaRegionCode, number> {
    return {
      NRPC: 0,
      SRPC: 0,
      ERPC: 0,
      WRPC: 0,
      NERPC: 0,
    };
  }
}
